import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import {
  Colors,
  StackChildren,
  Text,
  Spacing,
  fontFamily,
} from '@doordash/design-language'
import { withRouter } from 'react-router'
import t from './strings'
import { Interpolate } from '@doordash/lib-i18n'
import Loading from '@doordash/component-loading'

import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import Simple from '../../templates/simple'
import { SimpleStyleType } from '../../templates/simple/constants'
import { Root } from '../../pages/onboarding/styles'
import { Application } from '../../../utilities/application/application'

import * as actions from '../../../actions'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import { DasherPhone } from '../../atoms/icons'
import AppDownloadButton, {
  AppDownloadOS,
} from '../../atoms/app-download-button'
import { DownloadApp as Tag } from '../../../models/selectorAnchors'

import { getMobileDevice } from '../../../utilities/userAgent'

export const DasherPhoneContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`

export const NumberedList = styled.ol`
  padding-left: ${Spacing.Large}px;
  > li {
    ::marker {
      font-family: ${fontFamily};
      font-weight: ${Text.Weights.Bold};
      line-height: ${Text.LineHeights.Medium}px;
      color: ${Colors.TextSecondary};
    }
  }
`

type DownloadAppProps = {}

type ReduxProps = {
  errorMessage?: string
  clearErrors?: () => void
  loadApplication?: (uniqueLink: string) => Promise<void>
  loadDasher?: () => Promise<void>
  application?: Partial<Application>
  applicantUniqueLink?: string
  dasherId?: string
  track?: (action: DA.EventActions, name: DA.DownloadAppNames) => Promise<void>
}

const getOSType = (): AppDownloadOS => {
  switch (getMobileDevice()) {
    case 'iOS':
      return AppDownloadOS.IOS
    case 'Android':
      return AppDownloadOS.ANDROID
    default:
      return AppDownloadOS.DESKTOP
  }
}

const bodyListItems = [t.bodyList1(), t.bodyList2(), t.bodyList3()]

export const DownloadApp: React.FC<DownloadAppProps & ReduxProps> = ({
  loadDasher,
  loadApplication,
  applicantUniqueLink,
  dasherId,
  application,
  track,
}) => {
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    track(DA.EventActions.Rendered, DA.DownloadAppNames.DownloadApp)
  }, [track])

  React.useEffect(() => {
    const loadApplicantData = async () => {
      if (applicantUniqueLink === null) {
        await loadDasher()
      }

      if (applicantUniqueLink !== null && dasherId === null) {
        await loadApplication(applicantUniqueLink)
        setIsLoading(false)
      }

      // If unique link and dasher ID are already loaded,
      if (applicantUniqueLink && dasherId) {
        setIsLoading(false)
      }
    }

    loadApplicantData()
  }, [applicantUniqueLink, dasherId, loadDasher, loadApplication])

  return (
    <>
      {!isLoading ? (
        <StackChildren>
          <DasherPhoneContainer>
            <DasherPhone width={183} height={200} />
          </DasherPhoneContainer>

          <Text styles={Text.Styles.PageTitle2}>{t.title()}</Text>

          <Text styles={Text.Styles.Body1} color={Text.Colors.TextSecondary}>
            <Interpolate
              i18n={i18n}
              i18nKey={i18nKeys.excitement__download_app__body_content}
              values={{
                firstName: (application && application.firstName) || 'Dasher',
              }}
              components={[
                <Text
                  color={Text.Colors.TextSecondary}
                  display={Text.DisplayModes.Inline}
                  styles={Text.Styles.Body1}
                />,
              ]}
              data-anchor-id={Tag.Subheader}
            />
          </Text>

          <NumberedList>
            {bodyListItems.map((item, idx) => (
              <li key={`${item}${idx}`}>
                <Text
                  styles={Text.Styles.Body1}
                  color={Text.Colors.TextSecondary}
                >
                  {item}
                </Text>
              </li>
            ))}
          </NumberedList>

          <AppDownloadButton
            os={getOSType()}
            onClick={() =>
              track(DA.EventActions.Clicked, DA.DownloadAppNames.DownloadApp)
            }
            data-anchor-id={Tag.PrimaryAction}
          />
        </StackChildren>
      ) : (
        <Loading />
      )}
    </>
  )
}

export const mapStateToProps = ({
  errorMessage,
  application,
  applicantUniqueLink,
  dasherId,
}) => ({
  errorMessage,
  application,
  applicantUniqueLink,
  dasherId,
})

export const mapDispatchToProps = {
  clearErrors: actions.clearErrors,
  loadApplication: actions.loadApplication,
  loadDasher: actions.loadDasher,
  track: events.trackDownloadAppPage,
}

const ConnectedDownloadApp = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DownloadApp)
)

export const WrappedDownloadApp: React.FC<DownloadAppProps & ReduxProps> = ({
  errorMessage,
  clearErrors,
  ...props
}) => (
  <Simple
    includeFooter={false}
    headerIsDark={false}
    clearErrors={clearErrors}
    errorMessage={errorMessage}
    type={SimpleStyleType.Standard}
  >
    <Root>
      <Page>
        <PageRoot>
          <ConnectedDownloadApp {...props} />
        </PageRoot>
      </Page>
    </Root>
  </Simple>
)

export default ConnectedDownloadApp
